import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";
import { Bell, Gauge, Users } from "lucide-react";
import AppLayout from "@/components/AppLayout";
import PageHeader from "@/components/PageHeader";
import KpiCard from "@/components/KpiCard";
import DataTable, { Column } from "@/components/DataTable";
import { LoadingState } from "@/components/LoadingState";
import { dashboardApi } from "@/api/endpoints";
import type { BuildingDashboard, UnitMini } from "@/types";
import { formatNumber } from "@/utils/format";

export default function BuildingDashboardPage() {
  const { buildingId } = useParams<{ buildingId: string }>();
  const [data, setData] = useState<BuildingDashboard | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!buildingId) return;
    dashboardApi.building(buildingId).then((d) => {
      setData(d);
      setLoading(false);
    });
  }, [buildingId]);

  const columns: Column<UnitMini>[] = [
    { key: "unit_number", header: "Unit", render: (u) => u.unit_number },
    { key: "tenant", header: "Tenant", render: (u) => u.tenant_name ?? "—" },
    { key: "meters", header: "Meters", render: (u) => u.meter_count },
    {
      key: "consumption",
      header: "Last month consumption",
      render: (u) => formatNumber(u.last_month_consumption, 1),
    },
  ];

  return (
    <AppLayout>
      <div className="mb-4">
        <Link to="/buildings" className="text-sm text-brand-700 hover:underline">← Back to buildings</Link>
      </div>
      <PageHeader title="Building overview" subtitle="Consumption and tenant breakdown" />
      {loading || !data ? (
        <LoadingState />
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
            <KpiCard label="Units" value={data.unit_count} icon={<Users size={18} />} accent="blue" />
            <KpiCard label="Meters" value={data.meter_count} icon={<Gauge size={18} />} accent="slate" />
            <KpiCard label="Open alerts" value={data.unresolved_alert_count} icon={<Bell size={18} />}
                     accent={data.unresolved_alert_count ? "red" : "brand"} />
          </div>
          <div className="card p-5 mb-6">
            <h2 className="text-sm font-semibold text-slate-700 mb-3">Electricity (kWh) — last 6 months</h2>
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={data.trend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="month" stroke="#94a3b8" fontSize={12} />
                <YAxis stroke="#94a3b8" fontSize={12} />
                <Tooltip
                  formatter={(v: number) => formatNumber(v, 0)}
                  contentStyle={{ borderRadius: "8px", border: "1px solid #e2e8f0" }}
                />
                <Line type="monotone" dataKey="electricity_kwh" stroke="#0F766E" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <DataTable columns={columns} rows={data.units} emptyMessage="No units in this building" />
        </>
      )}
    </AppLayout>
  );
}
